import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
} from '@chakra-ui/react';
import { type KeyboardEvent, useContext, useEffect, useState } from 'react';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  updatePassword,
} from 'firebase/auth';
import type { FirebaseError } from 'firebase/app';
import { useMobileFormModal } from '@/hooks/useMobileFormModal';
import {
  EMAIL_DELIVERY_TOAST_HINT,
  errorToastOptions,
  getAuthErrorMessage,
  successToastOptions,
  validatePassword,
} from '@/helpers';
import { buildAuthContinueUrl } from '@/helpers/auth/authUrls';
import { useToast } from '@chakra-ui/react';
import { FirebaseContext } from '@/session/FirebaseContext';

export function ChangePasswordModal(props: { isOpen: boolean; onClose: () => void }) {
  const ctx = useContext(FirebaseContext);
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const { isMobile, modalSize, isCentered, scrollBehavior, contentProps, footerPb, titleFontSize } =
    useMobileFormModal();

  useEffect(() => {
    if (props.isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
      setSubmitAttempted(false);
      setIsBusy(false);
    }
  }, [props.isOpen]);

  async function handleChangePassword() {
    const user = ctx.user;
    if (!user?.email) return;

    setSubmitAttempted(true);
    setPasswordError('');

    if (currentPassword.length === 0) {
      setPasswordError('Current password is required.');
      return;
    }

    const pwCheck = validatePassword(newPassword, confirmPassword);
    if (!pwCheck.valid) {
      setPasswordError(pwCheck.error ?? 'Invalid password.');
      return;
    }

    setIsBusy(true);
    try {
      const cred = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, newPassword);
      toast(successToastOptions('Password updated', 'Your password has been changed.'));
      props.onClose();
    } catch (err) {
      toast(errorToastOptions('Failed to change password', getAuthErrorMessage(err as FirebaseError)));
    } finally {
      setIsBusy(false);
    }
  }

  async function handleSendResetEmail() {
    const user = ctx.user;
    if (!user?.email) return;

    setIsBusy(true);
    try {
      await sendPasswordResetEmail(ctx.auth, user.email, {
        url: buildAuthContinueUrl('/reset-password'),
        handleCodeInApp: true,
      });
      toast(
        successToastOptions(
          'Email sent',
          `If an account exists for this email, 10 Teams sent password reset instructions. ${EMAIL_DELIVERY_TOAST_HINT}`,
        ),
      );
    } catch (err) {
      toast(errorToastOptions('Failed to send email', getAuthErrorMessage(err as FirebaseError)));
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      isCentered={isCentered}
      size={modalSize}
      scrollBehavior={scrollBehavior}
    >
      <ModalOverlay />
      <ModalContent {...contentProps}>
        <ModalHeader>
          <Heading fontSize={titleFontSize}>Change password</Heading>
        </ModalHeader>
        <ModalCloseButton mt={2} mr={2} />
        <ModalBody px={{ base: 4, md: 6 }}>
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Current password</FormLabel>
              <InputGroup>
                <Input
                  size={isMobile ? 'lg' : 'md'}
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete='current-password'
                />
                <InputRightElement width='4.5rem'>
                  <Button h='1.75rem' size='sm' onClick={() => setShowCurrent(!showCurrent)}>
                    {showCurrent ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
            <FormControl isInvalid={submitAttempted && passwordError.length > 0} isRequired>
              <FormLabel>New password</FormLabel>
              <InputGroup>
                <Input
                  size={isMobile ? 'lg' : 'md'}
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete='new-password'
                />
                <InputRightElement width='4.5rem'>
                  <Button h='1.75rem' size='sm' onClick={() => setShowNew(!showNew)}>
                    {showNew ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup>
              {passwordError ? <FormErrorMessage>{passwordError}</FormErrorMessage> : null}
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Confirm new password</FormLabel>
              <InputGroup>
                <Input
                  size={isMobile ? 'lg' : 'md'}
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete='new-password'
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') void handleChangePassword();
                  }}
                />
                <InputRightElement width='4.5rem'>
                  <Button h='1.75rem' size='sm' onClick={() => setShowConfirm(!showConfirm)}>
                    {showConfirm ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
            <Button variant='link' size='sm' onClick={() => void handleSendResetEmail()} isDisabled={isBusy}>
              Send password reset email instead
            </Button>
          </Stack>
        </ModalBody>
        <ModalFooter pb={footerPb}>
          <Button
            w={{ base: 'full', md: 'auto' }}
            isLoading={isBusy}
            colorScheme='green'
            onClick={() => void handleChangePassword()}
          >
            Update password
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
